/**
 * Error Boundary for handling runtime errors gracefully
 * Specifically handles ReadableStream errors common in PWA/iOS Safari
 */
import React from 'react';
import { toast } from 'sonner';
import { AlertCircle, ChevronDown, Copy } from 'lucide-react';
import { isStudioDesign } from '@/lib/design';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    if (error?.message?.includes('ReadableStream') || error?.message?.includes('locked')) {
      console.warn('[ErrorBoundary] Suppressing ReadableStream error:', error.message);
      return { hasError: false, error: null, errorInfo: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (error?.message?.includes('ReadableStream') || error?.message?.includes('locked')) {
      console.warn('[ErrorBoundary] ReadableStream error caught:', error.message);
      return;
    }
    console.error('[ErrorBoundary] Error caught:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const errorMessage = error?.message || 'Unknown error';
      const componentStack = errorInfo?.componentStack || '';
      const stack = error?.stack || '';

      const copyDetails = () => {
        const text = `Error: ${errorMessage}\n\nComponent Stack:${componentStack}\n\nStack Trace:\n${stack}`;
        navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
      };

      // ---- Studio (doc 119 §24, mock frame 02): the 560 card on the ground ----
      if (isStudioDesign()) {
        return (
          <div className="st-au-shell">
            <div className="st-au-page">
              <div className="st-au-card st-au-w560">
                <div className="st-au-head" style={{ marginBottom: '18px' }}>
                  <h2 className="st-au-t">Something unexpected happened</h2>
                  <p className="st-au-s">Try refreshing the page. If the problem continues, contact support@worshipos.app.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => window.location.reload()} data-ui="primary-btn" className="st-au-btn st-au-pri">
                    Refresh page
                  </button>
                  <button
                    onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                    className="st-au-btn st-au-sec"
                  >
                    Try to continue
                  </button>
                </div>
                <details className="st-au-details" style={{ marginTop: '18px' }}>
                  <summary><ChevronDown />Show error details</summary>
                  <div className="st-au-notice st-au-bad">
                    <AlertCircle className="w-4 h-4" />
                    <span className="st-au-mono">{errorMessage}</span>
                  </div>
                  {componentStack && (
                    <>
                      <div className="st-au-lbl">Component stack</div>
                      <pre className="st-au-pre">{componentStack.trim()}</pre>
                    </>
                  )}
                  {stack && (
                    <>
                      <div className="st-au-lbl">Stack trace</div>
                      <pre className="st-au-pre">{stack}</pre>
                    </>
                  )}
                  <div>
                    <button onClick={copyDetails} className="st-au-btn st-au-sec st-au-sm">
                      <Copy />
                      Copy error details
                    </button>
                  </div>
                </details>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#1e1f22] p-4">
          <div className="bg-card rounded-lg shadow-lg p-6 max-w-2xl w-full">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">Something unexpected happened</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">Try refreshing the page. If the problem continues, contact support@worshipos.app.</p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  data-ui="primary-btn"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Refresh Page
                </button>
                <button
                  onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                  className="px-4 py-2 bg-slate-200 dark:bg-[#3e4145] text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-300"
                >
                  Try to Continue
                </button>
              </div>
            </div>
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100">
                Show error details (for troubleshooting)
              </summary>
              <div className="mt-2 space-y-3">
                <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md p-3">
                  <p className="text-sm font-mono text-red-700 dark:text-red-300 break-all">{errorMessage}</p>
                </div>
                {componentStack && (
                  <div className="bg-slate-50 dark:bg-[#1e1f22] border border-slate-200 dark:border-white/10 rounded-md p-3">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Component Stack:</p>
                    <pre className="text-xs font-mono text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{componentStack.trim()}</pre>
                  </div>
                )}
                {stack && (
                  <div className="bg-slate-50 dark:bg-[#1e1f22] border border-slate-200 dark:border-white/10 rounded-md p-3">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Stack Trace:</p>
                    <pre className="text-xs font-mono text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{stack}</pre>
                  </div>
                )}
                <button
                  onClick={() => {
                    const text = `Error: ${errorMessage}\n\nComponent Stack:${componentStack}\n\nStack Trace:\n${stack}`;
                    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
                  }}
                  className="text-xs px-3 py-1.5 bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300 rounded hover:bg-slate-200"
                >
                  Copy Error Details
                </button>
              </div>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
